import React, { useCallback, useEffect, useState } from "react";
import "./resellerStudio.css";
import { fetchResellerTree, type StudioResellerNode } from "./catalogueResellerApi";

type Props = { catalogueId: string };

/**
 * Studio view of reseller sharing: each reseller and the clients they forwarded to. Client numbers
 * are masked by the server; the studio sees names, dates, views and likes.
 */
const ResellerShareTree: React.FC<Props> = ({ catalogueId }) => {
  const [nodes, setNodes] = useState<StudioResellerNode[] | null>(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await fetchResellerTree(catalogueId);
      if (res.success && Array.isArray(res.data)) setNodes(res.data);
      else {
        setNodes([]);
        setError(res.message || "Could not load reseller shares.");
      }
    } catch {
      setNodes([]);
      setError("Could not load reseller shares.");
    }
  }, [catalogueId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (nodes === null) {
    return <div className="hkp-studio-tree-muted">Loading reseller shares…</div>;
  }

  return (
    <section className="hkp-studio-tree" aria-label="Reseller shares">
      <header className="hkp-studio-tree-head">
        <h3>Reseller shares</h3>
        <button type="button" className="hkp-studio-tree-refresh" onClick={() => void load()}>
          Refresh
        </button>
      </header>
      {error ? <p className="hkp-studio-tree-error">{error}</p> : null}
      {nodes.length === 0 && !error ? (
        <p className="hkp-studio-tree-muted">
          No reseller links yet. Share this catalogue with a customer marked as reseller.
        </p>
      ) : null}
      <ul className="hkp-studio-tree-list">
        {nodes.map((node) => {
          const isOpen = open[node.linkId] ?? node.clients.length > 0;
          return (
            <li key={node.linkId} className={`hkp-studio-tree-node${node.disabled ? " is-off" : ""}`}>
              <button
                type="button"
                className="hkp-studio-tree-row"
                aria-expanded={isOpen}
                onClick={() => setOpen((o) => ({ ...o, [node.linkId]: !isOpen }))}
              >
                <span className="hkp-studio-tree-caret" aria-hidden="true">
                  {isOpen ? "▾" : "▸"}
                </span>
                <span className="hkp-studio-tree-name" title={node.resellerName}>
                  {node.resellerName}
                </span>
                <span className="hkp-studio-tree-meta">
                  {node.forwardsUsed} of {node.forwardLimit} shares used
                  {node.disabled ? " · link off" : ""}
                </span>
              </button>
              {isOpen ? (
                node.clients.length === 0 ? (
                  <p className="hkp-studio-tree-muted hkp-studio-tree-indent">No clients yet.</p>
                ) : (
                  <table className="hkp-studio-tree-table">
                    <thead>
                      <tr>
                        <th scope="col">Client</th>
                        <th scope="col">Created</th>
                        <th scope="col">Expires</th>
                        <th scope="col" className="is-num">Views</th>
                        <th scope="col" className="is-num">Likes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {node.clients.map((c) => (
                        <tr key={c.linkId} className={c.status !== "ACTIVE" ? "is-off" : ""}>
                          <td>
                            <span className="hkp-studio-tree-client">{c.clientName}</span>
                            <span className="hkp-studio-tree-phone">
                              {c.clientPhoneMasked}
                              {c.status !== "ACTIVE" ? ` · ${c.status === "REVOKED" ? "revoked" : "expired"}` : ""}
                            </span>
                          </td>
                          <td>{shortDate(c.createdAt)}</td>
                          <td>{shortDate(c.expiresAt)}</td>
                          <td className="is-num">{c.views ?? 0}</td>
                          <td className="is-num">{c.likes ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

function shortDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default ResellerShareTree;
